import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/base/WorkCard';
import SectionHeading from '@/components/base/SectionHeading';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { works } from '@/mocks/works';
import { actor } from '@/mocks/actor';

export default function Detail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  useSeo({
    title: work ? `《${work.title}》详情 - 夏目彩春影视作品` : '作品详情 - 夏目彩春',
    description: work ? work.summary : '夏目彩春影视作品详情介绍。',
    keywords: work ? `夏目彩春,${work.title},${work.genre},${work.year}年作品` : '夏目彩春影视作品',
  });

  if (!work) {
    return (
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该影视作品"
          description="你访问的作品可能已被移除或链接有误。"
          actionLabel="返回作品分类"
          actionTo="/category"
        />
      </div>
    );
  }

  const meta = [
    { label: '类型', value: work.type, icon: 'ri-film-line' },
    { label: '导演', value: work.director, icon: 'ri-clapperboard-line' },
    { label: '主演', value: work.starring.join(' / '), icon: 'ri-user-star-line' },
    { label: '上映年份', value: `${work.year} 年`, icon: 'ri-calendar-line' },
    { label: '时长', value: work.duration, icon: 'ri-time-line' },
  ];

  const related = works
    .filter((w) => w.id !== work.id && (w.type === work.type || w.director === work.director))
    .slice(0, 4);

  return (
    <div className="bg-background-50">
      {/* Hero */}
      <div className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0 opacity-30">
          <img
            src={work.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover blur-2xl scale-110"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/80" />

        <div className="relative w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="[&_nav]:text-background-100/70 [&_span]:text-background-100/80 [&_a:hover]:text-accent-400">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '影视作品', to: '/category' }, { label: work.title }]}
            />
          </div>

          <div className="mt-7 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-7 md:gap-10 items-start">
            <div className="w-full max-w-[260px]">
              <img
                src={work.cover}
                alt={`${work.title} 封面`}
                title={`${work.title} - 夏目彩春影视作品`}
                className="w-full aspect-[3/4] object-top object-cover rounded-lg border border-background-50/10"
              />
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2.5 mb-4">
                <span className="px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-wide">
                  {work.type}
                </span>
                <span className="px-3 py-1 rounded-md bg-background-50/15 text-background-100 text-xs font-label">
                  {work.year}
                </span>
                <span className="px-3 py-1 rounded-md bg-background-50/15 text-background-100 text-xs">
                  {work.genre}
                </span>
              </div>
              <h1 className="font-heading font-black text-3xl md:text-5xl text-background-50 leading-tight">
                {work.title}
              </h1>
              <div className="mt-4 flex items-center gap-5">
                <span className="flex items-center gap-1.5 text-background-100">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="font-label text-lg">{work.rating.toFixed(1)}</span>
                  <span className="text-xs text-background-100/60">观众评分</span>
                </span>
              </div>
              <p className="mt-5 text-sm md:text-base text-background-100/85 leading-relaxed max-w-2xl">
                {work.summary}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/category"
                  className="px-5 py-2.5 rounded-md bg-background-50 text-foreground-900 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  返回分类页
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meta bar */}
      <div className="w-full max-w-content mx-auto px-4 md:px-8 -mt-6 relative z-10">
        <div className="bg-background-100 border border-background-200/80 rounded-lg p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {meta.map((m) => (
            <div key={m.label} className="flex items-start gap-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${m.icon} text-base`} />
              </span>
              <div className="min-w-0">
                <div className="text-xs text-foreground-400">{m.label}</div>
                <div className="text-sm text-foreground-900 mt-0.5 break-words">{m.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Synopsis */}
          <div className="lg:col-span-8">
            <h2 className="font-heading font-bold text-2xl text-foreground-950 mb-5 flex items-center gap-2.5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-loose">
              {work.synopsis.map((p, i) => (
                <p key={i} className={i === 0 ? 'first-letter:text-2xl first-letter:font-heading first-letter:text-primary-600' : ''}>
                  {p}
                </p>
              ))}
            </div>
          </div>

          {/* Actor highlight */}
          <aside className="lg:col-span-4">
            <div className="bg-background-100 border border-background-200/80 rounded-lg overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-user-star-line text-accent-500" />
                  演员介绍
                </h3>
              </div>
              <div className="px-5 pb-5">
                <div className="flex items-center gap-3.5">
                  <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-background-200">
                    <img
                      src={actor.portrait}
                      alt="夏目彩春"
                      title="夏目彩春 - 演员简介"
                      className="w-full h-full object-top object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-foreground-950">{actor.name}</div>
                    <div className="text-xs text-foreground-500 font-label mt-0.5">{actor.nameEn}</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-background-200/80">
                  <div className="text-xs text-foreground-400">本片角色</div>
                  <div className="text-sm font-medium text-primary-600 mt-1">{work.role}</div>
                  <p className="mt-2.5 text-sm text-foreground-700 leading-relaxed">{work.roleDesc}</p>
                </div>
                <Link
                  to="/about"
                  className="mt-4 flex items-center justify-center gap-1.5 w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  查看演员完整资料
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14 md:mt-20">
            <SectionHeading
              eyebrow="You May Also Like"
              title="相关影视作品推荐"
              description="根据同类型或同导演为你推荐，延续你的观影体验。"
              moreTo="/category"
              moreLabel="更多作品"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Link
            to="/category"
            className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回影视作品分类
          </Link>
        </div>
      </div>
    </div>
  );
}